import { Eye, EyeOff, TriangleAlert } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { getMangoApiClient } from '~/atoms/MangoApi'
import { Alert, FontStyle, Input, Text } from '~/components/core'
import { MangoButton } from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePartnerLogo } from '~/hooks/useCurrentPartner'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  ContentWrapper,
  ErrorWrapper,
  ForgotButton,
  ForgotButtonWrapper,
  InputWrapper,
  LoginButtonWrapper,
  LogoImage,
  PassButtonWrapper,
} from '~/pages/Login/Login.styles'
import { LoginBackground } from '~/pages/Login/LoginBackground'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { classifyPasswordResetConfirm } from '~/utils/passwordResetAnalytics'

interface ValidationState {
  minLength: boolean
  noWhitespace: boolean
  notNumeric: boolean
  passwordsMatch: boolean
}

const validate = (
  password: string,
  confirmPassword: string,
): ValidationState => ({
  minLength: password.length >= 15,
  noWhitespace: password === password.trim(),
  notNumeric: password.length > 0 && !/^\d+$/.test(password),
  passwordsMatch: password.length > 0 && password === confirmPassword,
})

const toggleButtonStyle: React.CSSProperties = {
  marginTop: '1px',
  cursor: 'pointer',
  width: '24px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  padding: 0,
}

export const ResetPasswordPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>()
  const partnerLogo = usePartnerLogo()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [generalError, setGeneralError] = useState('')
  const [tokenInvalid, setTokenInvalid] = useState(false)

  const validation = validate(password, confirmPassword)
  const allValid =
    validation.minLength &&
    validation.noWhitespace &&
    validation.notNumeric &&
    validation.passwordsMatch

  const handleSubmit = async () => {
    if (!allValid || !uidb64 || !token) return

    setIsLoading(true)
    setFieldErrors([])
    setGeneralError('')

    try {
      const api = getMangoApiClient()
      const response = await api.auth.confirmPasswordReset(
        uidb64,
        token,
        password,
      )
      const result = classifyPasswordResetConfirm(response)

      if (result.kind === 'completed') {
        track(AuthenticationAnalyticsEvents.PasswordResetCompleted)
        navigate(`${ApplicationRoutes.LOGIN}?passwordReset=1`)
        return
      }

      track(AuthenticationAnalyticsEvents.PasswordResetFailed, {
        stage: 'confirm',
        reasonCode: result.reasonCode,
        status: result.status,
      })

      if (result.reasonCode === 'token_invalid') {
        setTokenInvalid(true)
        return
      }
      if (result.fieldErrors) {
        setFieldErrors(result.fieldErrors)
        return
      }
      if (result.generalError) {
        setGeneralError(result.generalError)
        return
      }

      setGeneralError(
        t('common.genericError', 'Something went wrong. Please try again.'),
      )
    } catch {
      track(AuthenticationAnalyticsEvents.PasswordResetFailed, {
        stage: 'confirm',
        reasonCode: 'network',
      })
      setGeneralError(
        t('common.genericError', 'Something went wrong. Please try again.'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSubmit()
    }
  }

  if (tokenInvalid) {
    return (
      <LoginBackground>
        <ContentWrapper>
          <LogoImage alt="logo" src={partnerLogo} />
          <ErrorWrapper>
            <Alert
              icon={
                <TriangleAlert
                  size={24}
                  color="var(--chakra-colors-red-solid)"
                />
              }
            >
              {t(
                'resetPassword.tokenInvalid',
                'This link is invalid or has expired.',
              )}
            </Alert>
          </ErrorWrapper>
          <LoginButtonWrapper>
            <MangoButton
              onClick={() => navigate(ApplicationRoutes.FORGOT_PASSWORD)}
              size={Size.md}
            >
              {t('resetPassword.requestNewLink', 'Request a new link')}
            </MangoButton>
          </LoginButtonWrapper>
          <ForgotButtonWrapper>
            <ForgotButton onClick={() => navigate(ApplicationRoutes.LOGIN)}>
              <Text
                fontStyle={FontStyle.bSmall}
                text={t('common.backToLogin', 'Back to login')}
                color="var(--chakra-colors-brand-solid)"
              />
            </ForgotButton>
          </ForgotButtonWrapper>
        </ContentWrapper>
      </LoginBackground>
    )
  }

  return (
    <LoginBackground>
      <ContentWrapper>
        <LogoImage alt="logo" src={partnerLogo} />
        {generalError && (
          <ErrorWrapper>
            <Alert
              icon={
                <TriangleAlert
                  size={24}
                  color="var(--chakra-colors-red-solid)"
                />
              }
            >
              {generalError}
            </Alert>
          </ErrorWrapper>
        )}
        <InputWrapper>
          <Input
            id="password-input"
            placeholder={t(
              'resetPassword.newPasswordPlaceholder',
              'New password',
            )}
            type={showPassword ? 'text' : 'password'}
            name="new-password"
            autoComplete="new-password"
            value={password}
            onChange={setPassword}
            onKeyDown={handleKeyDown}
          />
          <PassButtonWrapper>
            <button
              type="button"
              aria-label={t(
                'resetPassword.togglePasswordVisibility',
                'Toggle password visibility',
              )}
              style={toggleButtonStyle}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff size={20} color="var(--chakra-colors-fg-muted)" />
              ) : (
                <Eye size={20} color="var(--chakra-colors-fg-muted)" />
              )}
            </button>
          </PassButtonWrapper>
        </InputWrapper>
        <InputWrapper>
          <Input
            id="confirm-password-input"
            placeholder={t(
              'resetPassword.confirmPasswordPlaceholder',
              'Confirm new password',
            )}
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirm-password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            onKeyDown={handleKeyDown}
          />
          <PassButtonWrapper>
            <button
              type="button"
              aria-label={t(
                'resetPassword.togglePasswordVisibility',
                'Toggle password visibility',
              )}
              style={toggleButtonStyle}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff size={20} color="var(--chakra-colors-fg-muted)" />
              ) : (
                <Eye size={20} color="var(--chakra-colors-fg-muted)" />
              )}
            </button>
          </PassButtonWrapper>
        </InputWrapper>
        {fieldErrors.length > 0 && (
          <div style={{ width: '90%', marginBottom: '8px' }}>
            {fieldErrors.map((err, i) => (
              <Text
                key={i}
                fontStyle={FontStyle.bSmall}
                text={err}
                color="var(--chakra-colors-red-solid)"
              />
            ))}
          </div>
        )}
        <div style={{ width: '90%', marginBottom: '16px' }}>
          <Text
            fontStyle={FontStyle.bSmall}
            text={`${validation.minLength ? '\u2713' : '\u2022'} ${t('resetPassword.validation.minLength', 'At least 15 characters')}`}
            color={
              validation.minLength
                ? 'var(--chakra-colors-green-solid)'
                : 'var(--chakra-colors-fg-muted)'
            }
          />
          <Text
            fontStyle={FontStyle.bSmall}
            text={`${validation.noWhitespace ? '\u2713' : '\u2022'} ${t('resetPassword.validation.noWhitespace', 'No leading or trailing spaces')}`}
            color={
              password.length === 0
                ? 'var(--chakra-colors-fg-muted)'
                : validation.noWhitespace
                  ? 'var(--chakra-colors-green-solid)'
                  : 'var(--chakra-colors-red-solid)'
            }
          />
          <Text
            fontStyle={FontStyle.bSmall}
            text={`${validation.notNumeric ? '\u2713' : '\u2022'} ${t('resetPassword.validation.notNumeric', 'Cannot be entirely numeric')}`}
            color={
              password.length === 0
                ? 'var(--chakra-colors-fg-muted)'
                : validation.notNumeric
                  ? 'var(--chakra-colors-green-solid)'
                  : 'var(--chakra-colors-red-solid)'
            }
          />
          <Text
            fontStyle={FontStyle.bSmall}
            text={`${validation.passwordsMatch ? '\u2713' : '\u2022'} ${t('resetPassword.validation.passwordsMatch', 'Passwords match')}`}
            color={
              confirmPassword.length === 0
                ? 'var(--chakra-colors-fg-muted)'
                : validation.passwordsMatch
                  ? 'var(--chakra-colors-green-solid)'
                  : 'var(--chakra-colors-red-solid)'
            }
          />
        </div>
        <LoginButtonWrapper>
          <MangoButton
            onClick={() => void handleSubmit()}
            disabled={!allValid || isLoading}
            isLoading={isLoading}
            size={Size.md}
          >
            {t('resetPassword.submitButton', 'Reset password')}
          </MangoButton>
        </LoginButtonWrapper>
        <ForgotButtonWrapper>
          <ForgotButton onClick={() => navigate(ApplicationRoutes.LOGIN)}>
            <Text
              fontStyle={FontStyle.bSmall}
              text={t('common.backToLogin', 'Back to login')}
              color="var(--chakra-colors-brand-solid)"
            />
          </ForgotButton>
        </ForgotButtonWrapper>
      </ContentWrapper>
    </LoginBackground>
  )
}
